import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/contexts/AuthContext';
import { API_BASE_URL, BACKEND_URL } from '../lib/api';
import axios from 'axios';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { AlertCircle, Shield, UserCheck, Loader2, Copy, Check, Download, Camera } from 'lucide-react';
import { copyToClipboard } from '../lib/utils';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [form, setForm] = useState({ fullName: '', email: '', mobile: '', password: '', confirmPassword: '', country: 'India', referralCode: refCode, acceptTerms: false, acceptPrivacy: false });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [creds, setCreds] = useState<{ username: string; password: string; referralCode: string } | null>(null);
  const [copied, setCopied] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const credsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!refCode) return;
    setLookingUp(true);
    axios.get(`${API_BASE_URL}/auth/referral/${refCode}`)
      .then(res => {
        if (res.data.valid) {
          setSponsorName(res.data.sponsorName);
        } else {
          setSponsorName('');
        }
      })
      .catch(() => { setSponsorName(''); })
      .finally(() => setLookingUp(false));
  }, [refCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (!form.acceptTerms || !form.acceptPrivacy) { setError('Accept terms and privacy policy'); return; }
    setError('');
    setIsLoading(true);
    try {
      const res = await register(form);
      const data = (res as any);
      if (data?.user) {
        setCreds({
          username: data.user.username,
          password: data.user.password || form.password,
          referralCode: data.user.referralCode,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const update = (field: string, value: any) => {
    setForm({ ...form, [field]: value });
    if (field === 'referralCode') {
      setSponsorName('');
      setError('');
      if (value) {
        setLookingUp(true);
        axios.get(`${API_BASE_URL}/auth/referral/${value}`)
          .then(res => {
            if (res.data.valid) {
              setSponsorName(res.data.sponsorName);
            }
          })
          .catch(() => {})
          .finally(() => setLookingUp(false));
      }
    }
  };

  const handleCopy = async (text: string, label: string) => {
    await copyToClipboard(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleDownload = () => {
    if (!creds) return;
    const text = `GR Trading Account Credentials\n================================\nUsername: ${creds.username}\nPassword: ${creds.password}\nReferral Code: ${creds.referralCode}\n================================\nKeep these safe!`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grtrading-credentials-${creds.username}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleScreenshot = async () => {
    if (!credsRef.current || !creds) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(credsRef.current, { backgroundColor: '#0B1628' });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `grtrading-credentials-${creds.username}.png`;
      a.click();
    } catch {
      handleCopy(`Username: ${creds.username}\nPassword: ${creds.password}\nReferral Code: ${creds.referralCode}`, 'all');
      setCopied('all');
    }
  };

  if (creds) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-fintech-900 px-4 py-8">
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8 animate-fade-up">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
                <span className="text-white font-bold text-base">GR</span>
              </div>
              <span className="text-white font-bold text-lg">GR Trading</span>
            </div>
            <h1 className="text-emerald-400 text-2xl font-bold">Registration Successful!</h1>
            <p className="text-slate-400 mt-2">Save your credentials below</p>
          </div>

          <div ref={credsRef} className="login-card-glass rounded-[20px] p-8 shadow-2xl shadow-black/20 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <p className="text-white font-semibold">Welcome, {form.fullName}!</p>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.08] space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Username</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold text-white">{creds.username}</span>
                    <button onClick={() => handleCopy(creds.username, 'username')} className="text-primary-400 hover:text-primary-300 transition-colors">
                      {copied === 'username' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div className="border-t border-white/[0.06]" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Password</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold text-white">{creds.password}</span>
                    <button onClick={() => handleCopy(creds.password, 'password')} className="text-primary-400 hover:text-primary-300 transition-colors">
                      {copied === 'password' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div className="border-t border-white/[0.06]" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Referral Code</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-semibold text-accent">{creds.referralCode}</span>
                    <button onClick={() => handleCopy(creds.referralCode, 'referral')} className="text-primary-400 hover:text-primary-300 transition-colors">
                      {copied === 'referral' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={handleDownload} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500/10 border border-primary-500/20 rounded-xl text-sm font-medium text-primary-400 hover:bg-primary-500/20 transition-colors">
                  <Download className="w-4 h-4" />Download
                </button>
                <button onClick={handleScreenshot} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white/[0.04] border border-white/10 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/[0.08] transition-colors">
                  <Camera className="w-4 h-4" />Screenshot
                </button>
              </div>

              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-400 text-center">
                  Save these credentials now. You will need them to log in.
                </p>
              </div>

              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-fintech-900 px-4 py-8">
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 animate-fade-up">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
              <span className="text-white font-bold text-base">GR</span>
            </div>
            <span className="text-white font-bold text-lg">GR Trading</span>
          </div>
          <h1 className="text-white text-3xl font-bold">Create Account</h1>
          <p className="text-slate-400 mt-2">Join GR Trading today</p>
        </div>

        <div className="login-card-glass rounded-[20px] p-8 shadow-2xl shadow-black/20 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400">
                <AlertCircle className="w-4 h-4 shrink-0" />{error}
              </div>
            )}
            <Input label="Full Name" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} required />
            <Input label="Email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
            <Input label="Mobile" value={form.mobile} onChange={(e) => update('mobile', e.target.value)} required />
            <Input label="Password" type="password" value={form.password} onChange={(e) => update('password', e.target.value)} required />
            <Input label="Confirm Password" type="password" value={form.confirmPassword} onChange={(e) => update('confirmPassword', e.target.value)} required />

            {refCode ? (
              <div>
                <Input label="Referral Code" value={form.referralCode} onChange={(e) => update('referralCode', e.target.value)} />
                {lookingUp ? (
                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                    <Loader2 className="w-3 h-3 animate-spin" />Looking up sponsor...
                  </div>
                ) : sponsorName ? (
                  <div className="flex items-center gap-2 mt-2 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <UserCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="text-sm text-emerald-400">Referred by <span className="font-semibold text-white">{sponsorName}</span></span>
                  </div>
                ) : form.referralCode ? (
                  <div className="flex items-center gap-2 mt-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="text-xs text-amber-400">Invalid referral code</span>
                  </div>
                ) : null}
              </div>
            ) : (
              <Input label="Referral Code (optional)" value={form.referralCode} onChange={(e) => update('referralCode', e.target.value)} />
            )}

            <div className="space-y-2.5">
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={form.acceptTerms} onChange={(e) => update('acceptTerms', e.target.checked)} className="mt-0.5 accent-primary-600" />
                <span className="text-slate-400">I accept the Terms & Conditions</span>
              </label>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                <input type="checkbox" checked={form.acceptPrivacy} onChange={(e) => update('acceptPrivacy', e.target.checked)} className="mt-0.5 accent-primary-600" />
                <span className="text-slate-400">I accept the Privacy Policy</span>
              </label>
            </div>
            <Button type="submit" isLoading={isLoading} className="w-full">Create Account</Button>
            <p className="text-center text-sm text-slate-400">
              Already have an account? <Link to="/login" className="text-accent hover:text-accent-light font-semibold transition-colors">Sign In</Link>
            </p>
          </form>
        </div>

        <div className="mt-6 flex items-center justify-center gap-1.5">
          <Shield className="w-3 h-3 text-slate-600" />
          <span className="text-xs text-slate-600">Secure registration</span>
        </div>
      </div>
    </div>
  );
}
